import React, { useState, useEffect, useCallback } from 'react';
import Grid from '@mui/material/Grid2';

import { SmartHash } from "@models";
import { TRUST_JSON_FOR_DATA_TYPES } from "@dndModels/DataTypeConstants";
import { DATA_TYPE_INFO } from "@dndModels/Constants";

import API from "@dndUtilities/system_api";

import LoadingBox from "@components/input/LoadingBox";
import ButtonBox from "@components/input/buttons/ButtonBox";
import { DataType } from '@dndModels/Types';

const data_type_map: { [data_type: string]: string } = {}; 
DATA_TYPE_INFO.filter(o => !o.player_object).forEach(o => {
  data_type_map[o.data_type] = o.plural;
}); 

const api = API.getInstance();

export default function AdminOnly() {
  const [fetched, setFetched] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loading_message, setLoadingMessage] = useState("Loading");
  const [percentage, setPercentage] = useState(0);
  const [hash_new, setHashNew] = useState<SmartHash>({});
  const [downloaded, setDownloaded] = useState<string[]>([]);
  const [downloaded_blocked, setDownloadedBlocked] = useState<string[]>([]);
  const [processing, setProcessing] = useState(false);
  
  const load = useCallback( async () => {
    // debugger;
    setFetched(true);
    setLoading(true);

    const onProgress = (data_type: string, percent: number ) => {
      setPercentage(percent * 100);
      setLoadingMessage(`Loading ${data_type_map[data_type]}`);
    };
    const onError = (message: string) => {
      setLoadingMessage(message);
    };
    
    let data_types = Object.keys(data_type_map) as DataType[];
    data_types = data_types.filter(o => !TRUST_JSON_FOR_DATA_TYPES.includes(o));
    const res = await api.getSetOfObjectsWithCallbacks({ 
      data_types, onProgress, onError 
    });
    setHashNew(res);
    setLoading(false);
  }, []);

  useEffect(() => {
    if(!fetched) {
      load();
    }
  }, [fetched, load]);

  const makeTextFile = (text: string) => {
    const data = new Blob([text], {type: 'text/json'});

    let textFile: any = null;
    // If we are replacing a previously generated file we need to
    // manually revoke the object URL to avoid memory leaks.
    if (textFile !== null) {
      window.URL.revokeObjectURL(textFile);
    }

    textFile = window.URL.createObjectURL(data);

    return textFile;
  };

  const sleep = (ms: number) => {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  const renderDownloadButton = (data_type: string, blocked: boolean, width: 3 | 12 = 3) => {
    if (blocked) {
      if (!downloaded_blocked.includes(data_type)) {
        return (
          <Grid
            key={data_type}
            size={{
              xs: width
            }}>
            <ButtonBox 
              name={data_type_map[data_type]} 
              disabled={processing}
              onClick={() => {
                const link = document.getElementById(`${data_type}_blocked_link`);
                if (link) {
                  link.click();
                }
                setDownloadedBlocked([...downloaded_blocked, data_type]);
              }} 
            />
          </Grid>
        );
      }
    } else {
      if (!downloaded.includes(data_type)) {
        return (
          <Grid
            key={data_type}
            size={{
              xs: width
            }}>
            <ButtonBox 
              name={data_type_map[data_type]} 
              disabled={processing}
              onClick={() => {
                const link = document.getElementById(`${data_type}_link`);
                if (link) {
                  link.click();
                }
                setDownloaded([...downloaded, data_type]);
              }} 
            />
          </Grid>
        );
      }
    }
    return null;
  }

  const renderHiddenDLButton = (data_type: string, blocked: boolean) => {
    // debugger;
    // console.log(data_type);
    // console.log(blocked);
    let sub_hash = hash_new[data_type];
    if (blocked) {
      sub_hash = sub_hash.filter(o => 
        o.owner_id === "" && !o.hb && o.blocked
      );
    } else {
      if (data_type === "floating_feature") {
      } else if (data_type === "creature_feature") {
      } else if (data_type === "template") {
      } else {
        sub_hash = sub_hash.filter(o => 
          o.owner_id === "" && !o.hb && !o.blocked
        );
      }
    }
        
    const data = data_type === "source_book" ? sub_hash.map(o => {
      return o.toDBObj();
    }) : sub_hash.map(o => {
      try {
        return o.toLight();
      } catch (ex) {
        debugger;
      }
      return null;
    }).filter(o => o !== null);
    return (
      <span key={`${data_type}${ blocked ? "_blocked" : ""}_hidden`}>
        <a id={`${data_type}${ blocked ? "_blocked" : ""}_link`} style={{ visibility: "hidden" }} download={`${data_type}.json`} href={makeTextFile(JSON.stringify(data))}>{ data_type_map[data_type] } { blocked && "Blocked"}</a>
      </span>
    );
  }
  
  if (!fetched || loading) {
    return (<div className="centered"><LoadingBox message={loading_message} percentage={percentage} size={window.innerHeight * 0.5} height={window.innerHeight - 81} /></div>); // width={window.innerWidth - 17}
  } else {
    const dts = Object.keys(data_type_map).filter(o => !TRUST_JSON_FOR_DATA_TYPES.includes(o) && o !== "floating_feature" && o !== "creature_feature");
    const dts_blocked = Object.keys(data_type_map).filter(o => !TRUST_JSON_FOR_DATA_TYPES.includes(o) && o !== "floating_feature" && o !== "creature_feature" && o !== "template");
    // console.log(data_type_map);
    // console.log(TRUST_JSON_FOR_DATA_TYPES);
    return (
      <Grid container spacing={0} direction="row">
        { !TRUST_JSON_FOR_DATA_TYPES.includes("floating_feature") && renderDownloadButton("floating_feature", false, 12) }
        { !TRUST_JSON_FOR_DATA_TYPES.includes("creature_feature") && renderDownloadButton("creature_feature", false, 12) }
        <Grid size={6}>
          <Grid container spacing={0} direction="row">
            <Grid
              className="centered"
              size={12}>
              Standard
            </Grid>
            <Grid
              className="centered"
              size={12}>
              <ButtonBox 
                name="Download All"
                disabled={processing}
                onClick={ async () => {
                  setProcessing(true);
                  for (const data_type of dts) {
                    const link = document.getElementById(`${data_type}_link`);
                    if (link) {
                      link.click();
                    }
                    await sleep(1000);
                  }
                  setDownloaded([...downloaded, ...dts]);
                  setProcessing(false);
                  return false;
                }}
              />
            </Grid>
            <Grid
              size={{
                xs: 12
              }}>&nbsp;</Grid>
            { dts.sort((a, b) => { return a.localeCompare(b); }).map(data_type => renderDownloadButton(data_type, false))}
            { dts.sort((a, b) => { return a.localeCompare(b); }).map(data_type => renderHiddenDLButton(data_type, false))}
          </Grid>
        </Grid>
        <Grid size={6}>
          <Grid container spacing={0} direction="row">
            <Grid
              className="centered"
              size={12}>
              Blocked
            </Grid>
            <Grid
              className="centered"
              size={12}>
              <ButtonBox 
                name="Download All"
                disabled={processing}
                onClick={ async () => {
                  setProcessing(true);
                  for (const data_type of dts_blocked) {
                    const link = document.getElementById(`${data_type}_blocked_link`);
                    if (link) {
                      link.click();
                    }
                    await sleep(1000);
                  }
                  setDownloadedBlocked([...downloaded_blocked, ...dts_blocked]);
                  setProcessing(false);
                  return false;
                }}
              />
            </Grid>
            <Grid size={12}>&nbsp;</Grid>
            { dts_blocked.sort((a, b) => { return a.localeCompare(b); }).map(data_type => renderDownloadButton(data_type, true))}
            { dts_blocked.sort((a, b) => { return a.localeCompare(b); }).map(data_type => renderHiddenDLButton(data_type, true))}
          </Grid>
        </Grid>
        { !TRUST_JSON_FOR_DATA_TYPES.includes("floating_feature") && renderHiddenDLButton("floating_feature", false) }
        { !TRUST_JSON_FOR_DATA_TYPES.includes("creature_feature") && renderHiddenDLButton("creature_feature", false) }
      </Grid>
    );
  }
}
